import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import React, { ComponentProps, ReactNode, useState, useEffect, useRef } from "react";
import { keyframes, styled } from "~/application/theme";
import useMobile from "~/presentation/shared/hooks/useMobile";
import {
  generateTooltipId,
  openTooltip,
  onTooltipOpen,
} from "~/application/utils/tooltipmanager-functions";

export type TooltipProps = Omit<ComponentProps<typeof ContentRoot>, "content"> & {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactElement;
  content: React.ReactNode;
};

const TooltipContent = ({ children }: { children: ReactNode }) => {
  return <>{children}</>;
};

const slideDownAndFade = keyframes({
  "0%": { opacity: 0, transform: "translateY(-4px)" },
  "100%": { opacity: 1, transform: "translateY(0)" },
});

const ArrowRoot = styled(TooltipPrimitive.Arrow, {});

const ContentRoot = styled(TooltipPrimitive.Content, {
  // Custom
  boxShadow: "$md",
  borderRadius: "$md",
  padding: "$2",
  zIndex: "$tooltip",
  maxWidth: 250,
  fontSize: "$sm",

  '&[data-state="delayed-open"]': {
    '&[data-side="top"]': { animationName: slideDownAndFade },
  },

  variants: {
    variant: {
      black: {
        color: "$neutrals-white",
        backgroundColor: "$neutrals-black",
        [`& ${ArrowRoot}`]: {
          fill: "$neutrals-black",
        },
      },
      white: {
        color: "$neutrals-darkest",
        backgroundColor: "$neutrals-white",
        [`& ${ArrowRoot}`]: {
          fill: "$neutrals-white",
        },
      },
      info: {
        color: "$neutrals-white",
        backgroundColor: "$primary-base",
        [`& ${ArrowRoot}`]: {
          fill: "$primary-base",
        },
      },
      error: {
        color: "$neutrals-white",
        backgroundColor: "$error-base",
        [`& ${ArrowRoot}`]: {
          fill: "$error-base",
        },
      },
      warning: {
        color: "$neutrals-black",
        backgroundColor: "$warning-base",
        [`& ${ArrowRoot}`]: {
          fill: "$warning-base",
        },
      },
      success: {
        color: "$neutrals-white",
        backgroundColor: "$success-base",
        [`& ${ArrowRoot}`]: {
          fill: "$success-base",
        },
      },
      infoLight: {
        color: "$neutrals-black",
        backgroundColor: "#DBEAFE",
        [`& ${ArrowRoot}`]: {
          fill: "#DBEAFE",
        },
      },
      warningLight: {
        color: "$warning-dark",
        backgroundColor: "$warning-light",
        border: "1px solid",
        [`& ${ArrowRoot}`]: {
          fill: "$warning-dark",
        },
      },
    },
  },

  defaultVariants: {
    variant: "black",
  },
});

export const Tooltip = ({
  children,
  content,
  open,
  defaultOpen,
  onOpenChange,
  ...props
}: TooltipProps) => {
  const isMobile = useMobile();
  const [isOpen, setIsOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const idRef = useRef<string>(generateTooltipId());

  useEffect(() => {
    if (!isMobile) return;
    return onTooltipOpen(idRef.current, () => setIsOpen(false));
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return;

    const isTooltipOpen = open !== undefined ? open : isOpen;
    if (!isTooltipOpen) return;

    const handleScroll = () => {
      if (open !== undefined) {
        onOpenChange?.(false);
      } else {
        setIsOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    document.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [isMobile, isOpen, open, onOpenChange]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!isMobile) {
      setIsOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isMobile) {
      if (open === false) return;
      e.preventDefault();
      e.stopPropagation();
      const nextOpen = !isOpen;
      if (nextOpen) openTooltip(idRef.current);
      setIsOpen(nextOpen);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (isMobile && isOpen) {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
    }
  };

  return (
    <div ref={tooltipRef} onClick={handleBackdropClick}>
      <TooltipPrimitive.Provider delayDuration={isMobile ? 0 : 200}>
        <TooltipPrimitive.Root
          open={open !== undefined ? open : isOpen}
          defaultOpen={defaultOpen}
          onOpenChange={handleOpenChange}
        >
          <TooltipPrimitive.Trigger asChild>
            <div onClick={handleClick}>{children}</div>
          </TooltipPrimitive.Trigger>

          <TooltipPrimitive.Portal>
            <ContentRoot
              side="top"
              align="center"
              sideOffset={5}
              onClick={(e) => e.stopPropagation()}
              {...props}
            >
              <TooltipContent>{content}</TooltipContent>
              <ArrowRoot width={8} height={4} />
            </ContentRoot>
          </TooltipPrimitive.Portal>
        </TooltipPrimitive.Root>
      </TooltipPrimitive.Provider>
    </div>
  );
};

Tooltip.displayName = "Tooltip";

export const TooltipLabel = styled("span", {
  // Reset
  all: "unset",

  // Custom
  fontSize: "inherit",
  fontWeight: "inherit",
});
